import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

export default function CreateListing() {
  const [cars, setCars] = useState([]);
  const [deletePrompt, setDeletePrompt] = useState(null);
  const [soldPrompt, setSoldPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const checkToken = () => {
      axios.get(`${process.env.REACT_APP_API_URL}/api/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {
        localStorage.removeItem('token');
        navigate('/admin/login');
      });
    };

    checkToken();

    const inactivityTimeout = 5 * 60 * 1000; // 5 minutes in ms
    let inactivityTimer;

    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        localStorage.removeItem('token');
        navigate('/admin/login');
        alert('Logged out due to inactivity.');
      }, inactivityTimeout);
    };

    resetTimer();

    window.addEventListener('mousemove', resetTimer);
    window.addEventListener('keydown', resetTimer);

    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/api/cars`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => {
        setCars(res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        setError('');
      })
      .catch(err => {
        console.error('Error fetching cars:', err);
        setError('Failed to load car listings. Please try again.');
      })
      .finally(() => setLoading(false));

    return () => {
      clearTimeout(inactivityTimer);
      window.removeEventListener('mousemove', resetTimer);
      window.removeEventListener('keydown', resetTimer);
    };
  }, [navigate]);

  const handleDelete = async (id) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/cars/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCars(cars.filter(car => car.id !== id));
      setDeletePrompt(null);
    } catch (err) {
      alert('Error deleting car');
    }
  };

  const handleMarkAsSold = async (id, car) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/cars/mark-sold`, {
        id,
        make: car.make,
        model: car.model,
        year: car.year,
        color: car.color,
        dateSold: new Date().toISOString(),
        timeSold: new Date().toTimeString(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCars(cars.map(c => c.id === id ? { ...c, sold: true } : c)); // Mark as sold locally
      setSoldPrompt(null);
    } catch (err) {
      alert('Error marking car as sold');
    }
  };

  if (!localStorage.getItem('token')) return null; // Prevent render if not logged in

  return (
    <div className="p-8 bg-white text-black font-fairplay">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Manage Car Listings</h2>
        <Link to="/admin/add-car" className="bg-white text-black px-4 py-2 rounded border border-cyan hover:border-magenta hover:scale-105 transition-all shadow-sm">
          Add New Car
        </Link>
      </div>
      {loading ? (
        <p className="text-center">Loading...</p>
      ) : error ? (
        <p className="text-red-600 text-center">{error}</p>
      ) : cars.length === 0 ? (
        <p className="text-center">No cars listed yet.</p>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2 text-left">Make</th>
              <th className="border p-2 text-left">Model</th>
              <th className="border p-2 text-left">Year</th>
              <th className="border p-2 text-left">Date Posted</th>
              <th className="border p-2 text-left">Status</th>
              <th className="border p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {cars.map(car => (
              <tr key={car.id} className="hover:bg-gray-50">
                <td className="border p-2">{car.make}</td>
                <td className="border p-2">{car.model}</td>
                <td className="border p-2">{car.year}</td>
                <td className="border p-2">{new Date(car.created_at).toLocaleDateString()}</td>
                <td className="border p-2">{car.sold ? <span className="bg-green-500 text-white px-2 py-1 rounded">Sold</span> : 'Available'}</td>
                <td className="border p-2 flex space-x-2">
                  <Link to={`/admin/edit-car/${car.id}`} className="bg-white text-black px-3 py-1 rounded border border-cyan hover:border-magenta hover:scale-105 transition-all shadow-sm">
                    Edit
                  </Link>
                  <button
                    onClick={() => setDeletePrompt(car.id)}
                    className="bg-white text-black px-3 py-1 rounded border border-magenta hover:border-cyan hover:scale-105 transition-all shadow-sm"
                  >
                    Delete
                  </button>
                  {!car.sold && (
                    <button
                      onClick={() => setSoldPrompt(car.id)}
                      className="bg-white text-black px-3 py-1 rounded border border-green-500 hover:border-green-700 hover:scale-105 transition-all shadow-sm"
                    >
                      Mark as Sold
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {deletePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <p className="mb-4 font-fairplay">Are you sure you want to delete this item?</p>
            <div className="flex space-x-4">
              <button
                onClick={() => handleDelete(deletePrompt)}
                className="bg-white text-black px-4 py-2 rounded border border-cyan hover:border-magenta hover:scale-105 transition-all shadow-sm font-fairplay"
              >
                Yes
              </button>
              <button
                onClick={() => setDeletePrompt(null)}
                className="bg-white text-black px-4 py-2 rounded border border-magenta hover:border-cyan hover:scale-105 transition-all shadow-sm font-fairplay"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
      {soldPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <p className="mb-4 font-fairplay">Are you sure you want to mark this car as sold?</p>
            <div className="flex space-x-4">
              <button
                onClick={() => {
                  const car = cars.find(c => c.id === soldPrompt);
                  if (car) handleMarkAsSold(soldPrompt, car);
                }}
                className="bg-white text-black px-4 py-2 rounded border border-green-500 hover:border-green-700 hover:scale-105 transition-all shadow-sm font-fairplay"
              >
                Yes
              </button>
              <button
                onClick={() => setSoldPrompt(null)}
                className="bg-white text-black px-4 py-2 rounded border border-magenta hover:border-cyan hover:scale-105 transition-all shadow-sm font-fairplay"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}